import { Checkbox, CheckboxGroup, Divider, Input, Radio, RadioGroup } from '@nextui-org/react';
import { useSearchParams } from 'next/navigation';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { components } from 'react-select';

import { IconCheckChip } from '@/assets';
import { Buttons, SelectMultipleWithSearch } from '@/components';
import { ClearTagIcon } from '@/components/assets';
import type { SelectOptionItem } from '@/components/select/SelectMultipleWithSearch';
import useCreateQueryString from '@/hooks/useCreateQueryString';
import useGetWidth from '@/hooks/useGetWidth';
import { getModels } from '@/services';
import { getTagsWithSearch } from '@/services/tags';
import type { ITag } from '@/types/tags';
import { useSQuery } from '@/utils';

const LIST_TYPE = [
  {
    key: 'all',
    title: 'All',
  },
  {
    key: 'image',
    title: 'Images',
  },
  {
    key: 'video',
    title: 'Videos',
  },
  {
    key: 'audio',
    title: 'Audio',
  },
];

interface FilterMarketPlaceProps {
  handleClearFilter?: () => void;
  setOpenFilter: Dispatch<SetStateAction<boolean>>;
}

const FilterMarketPlace: React.FC<FilterMarketPlaceProps> = ({
  handleClearFilter,
  setOpenFilter,
}) => {
  const searchParams = useSearchParams();
  const screenWidth = useGetWidth();

  const { data: dataModels } = useSQuery({
    queryKey: ['modelsFilter', 'market'],
    queryFn: () => getModels(30, 0),
    keepPreviousData: true,
  });

  const Option = (props: any) => {
    return props.isSelected ? (
      <components.Option
        {...props}
        children={
          <div className="flex h-full w-full items-center justify-between">
            {props?.data?.label} <IconCheckChip />
          </div>
        }
      />
    ) : (
      <components.Option {...props} />
    );
  };

  const { register, handleSubmit, setValue, reset } = useForm();
  const createQueryString = useCreateQueryString();

  const onSubmit = async (data: any) => {
    const { priceFrom, priceTo } = data;
    if (screenWidth < 768) {
      setOpenFilter(false);
    }
    createQueryString(['priceFrom', 'priceTo'], [Number(priceFrom), Number(priceTo)], {
      scroll: false,
    });
  };

  const loadTagOptions = async (search: string) => {
    try {
      const res: { data: ITag[] } = await getTagsWithSearch(search);
      const tags = mapOptionTags(res.data || []);
      return tags;
    } catch (err) {
      return [];
    }
  };

  const mapOptionTags = (tags: ITag[]): SelectOptionItem[] => {
    return tags.map((tag) => ({ value: tag.id, label: tag.content }));
  };

  useEffect(() => {
    if ((searchParams.get('priceFrom') as any) === 'string') {
      const priceFrom: any = searchParams.get('priceFrom');
      if (priceFrom) {
        setValue('priceFrom', priceFrom);
      }
    }
    if ((searchParams.get('priceTo') as any) === 'string') {
      const priceTo: any = searchParams.get('priceTo');
      if (priceTo) {
        setValue('priceTo', priceTo);
      }
    }
  }, []);

  useEffect(() => {
    if (!searchParams.toString()) {
      reset();
    }
  }, [searchParams.toString()]);

  const handleFilterType = (key: string) => {
    if (key === 'prompt')
      createQueryString(['prompt', 'page'], [true, 1], { scroll: false }, 'assetCategory');
    else if (key === 'all')
      createQueryString(['assetCategory', 'page'], ['', 1], { scroll: false }, 'prompt');
    else createQueryString(['assetCategory', 'page'], [key, 1], { scroll: false }, 'prompt');
  };

  const getDefaultValueType = () => {
    const assetCategory = searchParams.get('assetCategory');
    const isPrompt = searchParams.get('prompt');
    if (isPrompt) return 'prompt';
    if (!assetCategory) return 'all';
    return assetCategory;
  };

  const handleFilterModel = (val: Array<string>) => {
    createQueryString('modelIds', JSON.stringify(val), { scroll: false });
  };

  const handleFilterTags = (value: SelectOptionItem[]) => {
    createQueryString(
      ['tagIds', 'tags'],
      [
        JSON.stringify(value.map((items) => items.value)),
        JSON.stringify(value.map((item) => ({ label: item.label, value: item.value }))),
      ],
      { scroll: false },
    );
  };

  const getDefaultTags = () => {
    const tags = searchParams.get('tags');
    if (!tags) return [];
    return JSON.parse(tags);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="pr-[20px] md:p-4">
      <div>
        <p className="m-0 mb-5 text-16 font-semibold leading-6">Type</p>
        <RadioGroup
          defaultValue={getDefaultValueType()}
          onChange={(e) => handleFilterType(e.target.value)}
        >
          {LIST_TYPE.map((item) => (
            <Radio className="flex items-center" key={item.key} value={item.key}>
              {item.title}
            </Radio>
          ))}
        </RadioGroup>
        <Divider className="my-6 bg-gray-100" />
        <p className="m-0 mb-5 text-16 font-semibold leading-6">Model used</p>
        <CheckboxGroup
          defaultValue={
            (searchParams.get('modelIds') && JSON.parse(searchParams.get('modelIds') ?? '')) ??
            undefined
          }
          onChange={(e) => handleFilterModel(e as Array<string>)}
        >
          {dataModels?.data?.items.map((item: any, index: number) => (
            <Checkbox className="flex items-center" key={index} value={item.id}>
              {item.name}
            </Checkbox>
          ))}
        </CheckboxGroup>
        <Divider className="my-6 bg-gray-100" />
        <p className="m-0 mb-5 text-16 font-semibold leading-6">Tags</p>
        <SelectMultipleWithSearch
          value={getDefaultTags()}
          onChangeValue={handleFilterTags}
          optionsComponent={{
            Option,
            MultiValueRemove: (props: any) => (
              <components.MultiValueRemove {...props}>
                <div className="group">
                  <ClearTagIcon className="group-hover:fill-black" />
                </div>
              </components.MultiValueRemove>
            ),
          }}
          isValidNewOption={() => false}
          formatCreateLabel={() => undefined}
          styleProps={{
            indicatorSeparator: () => ({ display: 'none' }),
            menu: (baseStyles: any) => ({
              ...baseStyles,
              background: 'white',
              borderRadius: 10,
              border: 'none',
              padding: 8,
              zIndex: 99,
            }),
            control: (baseStyles: any) => ({
              ...baseStyles,
              background: '#F8F9FD',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              borderRadius: 25,
              padding: '0px 6px',
              fontSize: 14,
              minHeight: 40,
              cursor: 'pointer',
            }),
            multiValue: (baseStyles: any) => ({
              ...baseStyles,
              background: 'white',
              padding: '3px 7px',
              borderRadius: 100,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }),
          }}
          loadOptions={loadTagOptions}
          placeholder="Search option"
        />

        <Divider className="my-6 bg-gray-100" />
        <p className="m-0 mb-5 text-16 font-semibold leading-6">Price range (AIRI)</p>
        <div className="flex">
          <div className="mr-3 w-full rounded-[100px] bg-gray-50">
            <Input
              defaultValue={(searchParams.get('priceFrom') as any) ?? undefined}
              type="number"
              classNames={{
                base: 'flex justify-center',
                input: [
                  'dark:placeholder:text-white font-normal text-14 pl-1 disabled:cursor-not-allowed',
                  'rounded-[100px]',
                ],
                innerWrapper: ['bg-transparent', 'rounded-[100px]'],
                inputWrapper: ['bg-transparent', 'rounded-[100px]'],
              }}
              placeholder="Min"
              {...register('priceFrom')}
            />
          </div>
          <div className="ml-3 w-full rounded-[100px] bg-gray-50">
            <Input
              defaultValue={searchParams.get('priceTo') ?? undefined}
              type="number"
              classNames={{
                base: 'flex justify-center',
                input: [
                  'dark:placeholder:text-white font-normal text-14 pl-1 disabled:cursor-not-allowed',
                  'rounded-[100px]',
                ],
                innerWrapper: ['bg-transparent', 'rounded-[100px]'],
                inputWrapper: ['bg-transparent', 'rounded-[100px]'],
              }}
              placeholder="Max"
              {...register('priceTo')}
            />
          </div>
        </div>
        <div className="my-4 flex">
          <Buttons
            onClick={handleClearFilter}
            typeBtn="cancel"
            type="submit"
            className="mr-6 hidden w-1/2 text-13 font-semibold md:block"
          >
            Reset all
          </Buttons>
          <Buttons
            typeBtn="secondary"
            type="submit"
            className="w-full text-13 font-semibold text-primary md:w-1/2"
          >
            Apply
          </Buttons>
        </div>
      </div>
    </form>
  );
};

export default FilterMarketPlace;
